import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Settings, 
  Clock, 
  Mail, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff,
  Loader,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Wrench,
  Hammer,
  MessageCircle,
  Phone,
  HelpCircle,
  Info,
  Shield,
  Zap,
  Calendar,
  User,
  Users,
  AlertTriangle
} from 'lucide-react';
import { maintenancePageAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import AdminSectionWrapper from './AdminSectionWrapper';

const MaintenancePageManagement = () => {
  const { notify } = useNotificationHook();
  const [content, setContent] = useState({
    main_icon: 'Settings',
    title: 'We\'ll be back soon!',
    subtitle: 'We\'re currently performing some maintenance on our site.',
    description: 'We\'re working hard to improve your experience. Please check back soon.',
    estimated_time_title: 'Estimated Time',
    estimated_time_value: '2 hours',
    progress_title: 'Progress',
    progress_value: '65%',
    recent_updates_title: 'Recent Updates',
    recent_updates: [],
    help_title: 'Need Help?',
    help_options: [],
    show_service_status: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [activeSection, setActiveSection] = useState('main');
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [editingHelpOption, setEditingHelpOption] = useState(null);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [showHelpForm, setShowHelpForm] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await maintenancePageAPI.getAdminContent();
      if (response?.data?.success) {
        setContent(response.data.data);
        setAvailableIcons(response.data.data.availableIcons || []);
      }
    } catch (error) {
      console.error('Error fetching maintenance page content:', error);
      notify.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await maintenancePageAPI.updateContent(content);
      if (response?.data?.success) {
        notify.success('Maintenance page content updated successfully!');
      }
    } catch (error) {
      console.error('Error saving maintenance page content:', error);
      notify.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all content to defaults?')) {
      try {
        setSaving(true);
        const response = await maintenancePageAPI.resetContent();
        if (response?.data?.success) {
          await fetchContent();
          notify.success('Content reset to defaults successfully!');
        }
      } catch (error) {
        console.error('Error resetting content:', error);
        notify.error('Failed to reset content');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleAddUpdate = () => {
    setEditingUpdate(null);
    setShowUpdateForm(true);
  };

  const handleEditUpdate = (update) => {
    setEditingUpdate(update);
    setShowUpdateForm(true);
  };

  const handleDeleteUpdate = (index) => {
    if (window.confirm('Are you sure you want to delete this update?')) {
      const newUpdates = [...content.recent_updates];
      newUpdates.splice(index, 1);
      setContent({ ...content, recent_updates: newUpdates });
    }
  };

  const handleSaveUpdate = (updateData) => {
    const newUpdates = [...content.recent_updates];
    if (editingUpdate) {
      const index = newUpdates.findIndex(u => u.id === editingUpdate.id);
      if (index !== -1) {
        newUpdates[index] = { ...editingUpdate, ...updateData };
      }
    } else {
      newUpdates.push({
        id: Date.now(),
        ...updateData
      });
    }
    setContent({ ...content, recent_updates: newUpdates });
    setShowUpdateForm(false);
    setEditingUpdate(null);
  };

  const handleAddHelpOption = () => {
    setEditingHelpOption(null);
    setShowHelpForm(true);
  };

  const handleEditHelpOption = (option) => {
    setEditingHelpOption(option);
    setShowHelpForm(true);
  };

  const handleDeleteHelpOption = (index) => {
    if (window.confirm('Are you sure you want to delete this help option?')) {
      const newOptions = [...content.help_options];
      newOptions.splice(index, 1);
      setContent({ ...content, help_options: newOptions });
    }
  };

  const handleSaveHelpOption = (optionData) => {
    const newOptions = [...content.help_options];
    if (editingHelpOption) {
      const index = newOptions.findIndex(o => o.id === editingHelpOption.id);
      if (index !== -1) {
        newOptions[index] = { ...editingHelpOption, ...optionData };
      }
    } else {
      newOptions.push({
        id: Date.now(),
        ...optionData
      });
    }
    setContent({ ...content, help_options: newOptions });
    setShowHelpForm(false);
    setEditingHelpOption(null);
  };

  const getIconComponent = (iconName) => {
    const iconMap = {
      Settings, Gear: Settings, Wrench, Hammer,
      Mail, MessageCircle, Phone, HelpCircle, Info, AlertCircle,
      Clock, Calendar, User, Users, Shield, Zap, RefreshCw,
      Loader, CheckCircle, XCircle, AlertTriangle
    };
    return iconMap[iconName] || Settings;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <AdminSectionWrapper>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Maintenance Page Management
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1">
            Manage all content and settings for the maintenance mode page
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            onClick={handleReset}
            className="px-3 sm:px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm sm:text-base"
          >
            <span className="hidden sm:inline">Reset to Defaults</span>
            <span className="sm:hidden">Reset</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
            : 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'main', label: 'Main Section', icon: Settings },
            { id: 'estimated', label: 'Estimated Time', icon: Clock },
            { id: 'updates', label: 'Recent Updates', icon: RefreshCw },
            { id: 'help', label: 'Need Help', icon: Mail },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeSection === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <IconComponent className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 sm:p-6">
          {/* Main Section */}
          {activeSection === 'main' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Main Section
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Main Icon
                  </label>
                  <select
                    value={content.main_icon}
                    onChange={(e) => setContent({ ...content, main_icon: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {availableIcons.map((icon) => (
                      <option key={icon.name} value={icon.name}>
                        {icon.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={content.title}
                    onChange={(e) => setContent({ ...content, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="We'll be back soon!"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={content.subtitle}
                  onChange={(e) => setContent({ ...content, subtitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="We're currently performing some maintenance on our site."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={content.description}
                  onChange={(e) => setContent({ ...content, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="We're working hard to improve your experience. Please check back soon."
                />
              </div>
            </div>
          )}

          {/* Estimated Time Section */}
          {activeSection === 'estimated' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Estimated Time Section
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Estimated Time Title
                  </label>
                  <input
                    type="text"
                    value={content.estimated_time_title}
                    onChange={(e) => setContent({ ...content, estimated_time_title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Estimated Time"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Estimated Time Value
                  </label>
                  <input
                    type="text"
                    value={content.estimated_time_value}
                    onChange={(e) => setContent({ ...content, estimated_time_value: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="2 hours"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Progress Title
                  </label>
                  <input
                    type="text"
                    value={content.progress_title}
                    onChange={(e) => setContent({ ...content, progress_title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Progress"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Progress Value
                  </label>
                  <input
                    type="text"
                    value={content.progress_value}
                    onChange={(e) => setContent({ ...content, progress_value: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="65%"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Recent Updates Section */}
          {activeSection === 'updates' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Recent Updates Section
                </h3>
                <button
                  onClick={handleAddUpdate}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Update
                </button>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Section Title
                </label>
                <input
                  type="text"
                  value={content.recent_updates_title}
                  onChange={(e) => setContent({ ...content, recent_updates_title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Recent Updates"
                />
              </div>

              {/* Updates List */}
              <div className="space-y-4">
                {content.recent_updates.map((update, index) => (
                  <div
                    key={update.id}
                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {update.time}
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {update.text}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditUpdate(update)}
                        className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUpdate(index)}
                        className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {content.recent_updates.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No updates added yet. Click "Add Update" to get started.
                  </div>
                )}
              </div>

              {/* Add/Edit Update Form */}
              {showUpdateForm && (
                <UpdateForm
                  update={editingUpdate}
                  onSave={handleSaveUpdate}
                  onCancel={() => {
                    setShowUpdateForm(false);
                    setEditingUpdate(null);
                  }}
                />
              )}
            </div>
          )}

          {/* Need Help Section */}
          {activeSection === 'help' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Need Help Section
                </h3>
                <button
                  onClick={handleAddHelpOption}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Help Option
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Help Section Title
                  </label>
                  <input
                    type="text"
                    value={content.help_title}
                    onChange={(e) => setContent({ ...content, help_title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Need Help?"
                  />
                </div>
              </div>

              {/* Help Options List */}
              <div className="space-y-4">
                {content.help_options.map((option, index) => (
                  <div
                    key={option.id}
                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-3">
                        {(() => {
                          const IconComponent = getIconComponent(option.icon || 'Mail');
                          return <IconComponent className="w-5 h-5 text-gray-500" />;
                        })()}
                        <div>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {option.title}
                          </span>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {option.value}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {option.link}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditHelpOption(option)}
                        className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteHelpOption(index)}
                        className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {content.help_options.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No help options added yet. Click "Add Help Option" to get started.
                  </div>
                )}
              </div>

              {/* Add/Edit Help Option Form */}
              {showHelpForm && (
                <HelpOptionForm
                  option={editingHelpOption}
                  onSave={handleSaveHelpOption}
                  onCancel={() => {
                    setShowHelpForm(false);
                    setEditingHelpOption(null);
                  }}
                />
              )}
            </div>
          )}

          {/* Settings Section */}
          {activeSection === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Settings
              </h3>
              
              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content.show_service_status}
                    onChange={(e) => setContent({ ...content, show_service_status: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Show Service Status Section
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminSectionWrapper>
  );
};

// Update Form Component
const UpdateForm = ({ update, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    time: update?.time || '',
    text: update?.text || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
      <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
        {update ? 'Edit' : 'Add'} Update
      </h4>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Time *
          </label>
          <input
            type="text"
            name="time"
            value={formData.time}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="e.g., 2:30 PM"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Update Text *
          </label>
          <textarea
            name="text"
            value={formData.text}
            onChange={handleChange}
            required
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="e.g., Database optimization completed"
          />
        </div>
        <div className="flex gap-3">
          <button
            type="submit"
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {update ? 'Update' : 'Add'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// Help Option Form Component
const HelpOptionForm = ({ option, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: option?.title || '',
    value: option?.value || '',
    link: option?.link || '',
    icon: option?.icon || 'Mail'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
      <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
        {option ? 'Edit' : 'Add'} Help Option
      </h4>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Title *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="e.g., Phone Support"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Value/Description *
          </label>
          <input
            type="text"
            name="value"
            value={formData.value}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="e.g., +1-555-0123"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Icon
          </label>
          <select
            name="icon"
            value={formData.icon}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="Mail">Mail</option>
            <option value="Phone">Phone</option>
            <option value="MessageCircle">Message Circle</option>
            <option value="HelpCircle">Help Circle</option>
            <option value="Info">Info</option>
            <option value="Shield">Shield</option>
            <option value="User">User</option>
            <option value="Users">Users</option>
            <option value="Clock">Clock</option>
            <option value="Calendar">Calendar</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Link URL *
          </label>
          <input
            type="text"
            name="link"
            value={formData.link}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="e.g., tel:+1-555-0123"
          />
        </div>
        <div className="flex gap-3">
          <button
            type="submit"
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {option ? 'Update' : 'Add'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default MaintenancePageManagement; 